import { DragDropContext, Draggable, Droppable, DropResult } from "@hello-pangea/dnd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import { Button } from "../../components/Button";
import { MediumCircleContainer } from "../../components/CircleContainer/styles";
import ConfirmModal from "../../components/ConfirmModal";
import FixedFooterBar from "../../components/FixedFooterBar";
import * as Fonts from "../../components/Fonts";
import { LoadingModal } from "../../components/LoadingModal";
import { PlusPromo } from "../../components/PlusPromo";
import { notifySaveError } from "../../components/SaveErrorNotice";
import { SubHeader } from "../../components/SubHeader";
import { useToast } from "../../components/Toast";
import ToggleSwitch from "../../components/ToggleSwitch";
import { useAppState } from "../../contexts/App";
import { useSite } from "../../contexts/Site";
import { PAGES_PATH, withPage } from "../../hooks/CurrentPage";
import { useDiscardDraftExit } from "../../hooks/DiscardDraftExit";
import CalendarIcon from "../../icons/Calendar";
import PlusIcon from "../../icons/Plus";
import {
  canHaveMenu,
  duplicateHome,
  duplicatePage,
  getPages,
  isAgendaPage,
  MAX_PAGES,
  newAgendaPage,
  siteHasAgendaPage,
  withPages,
} from "../../model/pages";
import { Site, SitePage } from "../../model/site";
import { api, hasActiveSubscription } from "../../services/api";
import CreationProgress from "../Editor/components/CreationProgress";
import { ListCard, ListCardAction, VisibilityIcon } from "../Editor/components/ListCard";
import { RowActions } from "../Editor/components/RowActions";
import { AddSectionCard, ContinueButtonSection } from "../Editor/pages/Sections/components/SectionsList/styles";
import { Container, Section } from "../Settings/styles";
import { BarAdd, BarSpace, HomeTag, LimitNotice, MenuRow, PageItem, PageList } from "./styles";

const HomeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
    <path d="M3 10.5 12 3l9 7.5V20a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1z" strokeLinejoin="round" />
  </svg>
);

const PageIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" strokeLinejoin="round" />
    <path d="M14 3v5h5M9 13h6M9 17h4" strokeLinecap="round" />
  </svg>
);

/**
 * The pages of the Keepo (/editor/pages): the menu, the home and the pages in their menu order, and their actions.
 * Any plan builds them; only PLUS publishes them (publishing as FREE leaves them saved, off the published Keepo).
 * It's the editor's way in and out, creating the Keepo or editing it once published: every way in leads here, each
 * page's sections (the home's too) come back here, and going on leads to the style, then publishing.
 */
export const PagesPage = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const { appState, setAppState } = useAppState();
  const { site, setSite } = useSite();
  const [deleting, setDeleting] = useState<SitePage>();
  const prefix = "pages.editor.pages";
  const creating = !appState.published;
  const base = PAGES_PATH;
  // leaving the editor of a published Keepo with changes not published: discard them or stay
  const { exitMessages, discardAndLeave, removingDraft, discardFailed } = useDiscardDraftExit();

  const pages = getPages(site);
  const atLimit = pages.length >= MAX_PAGES;
  const addPage = () => !atLimit && navigate(`${base}/edit`);
  // the pages done: the Keepo's style, then publishing it
  const goOn = () => navigate("/editor/style?flow=publish");

  const save = (next: Site, onSaved?: () => void) => {
    setSite(next);
    // a change not published yet: leaving the editor asks about it, as with sections
    setAppState({ ...appState, editingSiteModified: true });
    api
      .saveDraft(appState.editingSiteId as string, next)
      .then(() => onSaved?.())
      .catch(notifySaveError);
  };
  // the menu follows: off when only the home is left (see withPages)
  const savePages = (next: SitePage[]) => save(withPages(site, next));

  const moveTo = (from: number, to: number) => {
    const next = [...pages];
    const [page] = next.splice(from, 1);
    next.splice(to, 0, page);
    savePages(next);
  };

  const onDragEnd = ({ source, destination }: DropResult) => {
    if (destination && destination.index !== source.index) {
      moveTo(source.index, destination.index);
    }
  };

  const toggleMenu = (page: SitePage) =>
    savePages(pages.map(p => (p.id === page.id ? { ...p, showInMenu: !p.showInMenu } : p)));

  // one agenda page per Keepo, with only the agenda: it is always on the list, hidden until its eye adds it (and puts
  // it in the menu); its content is the Agenda screen
  const hasAgenda = pages.some(isAgendaPage);
  // publishing a FREE Keepo with the Agenda (set up freely meanwhile) sends here: subscribe, or the Agenda's page goes
  const [searchParams] = useSearchParams();
  const agendaNeedsPlus =
    searchParams.get("plusRequired") === "agenda" &&
    siteHasAgendaPage(site) &&
    !hasActiveSubscription(appState.editingSiteSubscription);
  // the agenda page's eye only puts its page in or out of the menu, in the draft (like any other page's): the Agenda
  // itself is turned on or off when the Keepo is published (publishSite), so nothing here calls its API, nothing waits,
  // and nothing that isn't on the air yet is promised: the toast comes once the draft is saved, and says "when you publish"
  const isPlus = hasActiveSubscription(appState.editingSiteSubscription);
  const agendaToast = (inMenu: boolean) =>
    t(`${prefix}.agenda.${!inMenu ? "turnedOff" : isPlus ? "turnedOn" : "turnedOnNoPlus"}`);
  const addAgenda = () => {
    if (atLimit || hasAgenda) return;
    save(withPages(site, [...pages, newAgendaPage(pages, t(`${prefix}.agenda.title`))]), () =>
      toast.success(agendaToast(true))
    );
  };
  const toggleAgendaMenu = (page: SitePage) => {
    const inMenu = !page.showInMenu;
    save(
      withPages(
        site,
        pages.map(p => (p.id === page.id ? { ...p, showInMenu: inMenu } : p))
      ),
      () => toast.success(agendaToast(inMenu))
    );
  };
  const duplicate = (page: SitePage) => savePages([...pages, duplicatePage(page, pages, t(`${prefix}.copySuffix`))]);
  const duplicateHomePage = () =>
    savePages([...pages, duplicateHome(site, pages, t(`${prefix}.home`), t(`${prefix}.copySuffix`))]);
  // the menu needs a page in it: only the home, or every page hidden, and it can't be turned on
  const menuAllowed = canHaveMenu(pages);

  const sectionsCount = (page: SitePage) =>
    page.sections.length === 1
      ? t(`${prefix}.sectionsOne`)
      : t(`${prefix}.sectionsMany`, { count: page.sections.length });

  return (
    <Container>
      {/* where the person is in the flow, on top; the screen's own header under it */}
      <CreationProgress step="pages" />
      <SubHeader
        title={t(`${prefix}.title`)}
        // creating it: back to how it was started (the step before); editing it: out of the editor, to the Keepo
        crumbs={creating ? [{ label: t("pages.editor.creationProgress.screens.start"), to: "/editor/start" }] : []}
        hasUnsavedChanges={!creating && appState.editingSiteModified}
        onConfirmExitUnsavedChanges={discardAndLeave}
        confirmExitModal={exitMessages}
      />
      {agendaNeedsPlus && (
        <Section>
          <PlusPromo
            compact
            title={t(`${prefix}.agenda.plusRequired`)}
            onClick={() => navigate(`/subscription/features?site=${appState.editingSiteId}`)}
          />
        </Section>
      )}

      <Section>
        <Fonts.Label>{t(`${prefix}.counter`, { count: pages.length, max: MAX_PAGES })}</Fonts.Label>

        <PageList>
          <PageItem aria-label={t(`${prefix}.home`)}>
            <ListCard
              hideDragHandle
              title={t(`${prefix}.home`)}
              // the Keepo's main page: said by a tag by its name, its address alone under it
              titleExtra={<HomeTag>{t(`${prefix}.homeTag`)}</HomeTag>}
              description="/"
              leading={<HomeIcon />}
              leadingVariant="icon"
              onClick={() => navigate("/editor/sections")}
              actions={
                <RowActions
                  label={t(`${prefix}.actions.more`, { title: t(`${prefix}.home`) })}
                  actions={[
                    // its details are the Keepo's own (name, description, sharing): its search and sharing screen
                    { label: t(`${prefix}.actions.edit`), onSelect: () => navigate("/seo") },
                    { label: t(`${prefix}.actions.duplicate`), disabled: atLimit, onSelect: duplicateHomePage },
                  ]}
                />
              }
            />
          </PageItem>
        </PageList>

        {/* @ts-ignore */}
        <DragDropContext onDragEnd={onDragEnd}>
          {/* @ts-ignore */}
          <Droppable droppableId="pages" direction="vertical">
            {provided => (
              <PageList {...provided.droppableProps} ref={provided.innerRef}>
                {pages.map((page, index) => {
                  const menuLabel = page.showInMenu ? t(`${prefix}.actions.hide`) : t(`${prefix}.actions.show`);
                  return (
                    // @ts-ignore
                    <Draggable key={page.id} index={index} draggableId={page.id}>
                      {dragging => (
                        <PageItem ref={dragging.innerRef} {...dragging.draggableProps} aria-label={page.title}>
                          {/* the drag handle is the card (the library makes it a button): the row keeps its list role */}
                          <div {...dragging.dragHandleProps}>
                            <ListCard
                              title={page.title}
                              description={`/${page.slug} · ${sectionsCount(page)}`}
                              leading={<PageIcon />}
                              leadingVariant="icon"
                              dimmed={!page.showInMenu}
                              // into the page: its structure (sections) in the editor; the agenda's is the Agenda screen
                              onClick={() =>
                                navigate(isAgendaPage(page) ? "/agenda" : withPage("/editor/sections", page.id))
                              }
                              actions={
                                <>
                                  {/* in the menu or not: the eye, as on the editor's sections */}
                                  <ListCardAction
                                    type="button"
                                    title={menuLabel}
                                    aria-label={menuLabel}
                                    onClick={event => {
                                      event.stopPropagation();
                                      if (isAgendaPage(page)) toggleAgendaMenu(page);
                                      else toggleMenu(page);
                                    }}>
                                    <VisibilityIcon visible={page.showInMenu} />
                                  </ListCardAction>
                                  <RowActions
                                    label={t(`${prefix}.actions.more`, { title: page.title })}
                                    actions={[
                                      {
                                        label: t(`${prefix}.actions.edit`),
                                        onSelect: () => navigate(`${base}/edit?id=${page.id}`),
                                      },
                                      {
                                        label: t(`${prefix}.actions.up`),
                                        disabled: index === 0,
                                        onSelect: () => moveTo(index, index - 1),
                                      },
                                      {
                                        label: t(`${prefix}.actions.down`),
                                        disabled: index === pages.length - 1,
                                        onSelect: () => moveTo(index, index + 1),
                                      },
                                      // only one agenda page: it has no copy
                                      ...(isAgendaPage(page)
                                        ? []
                                        : [
                                            {
                                              label: t(`${prefix}.actions.duplicate`),
                                              disabled: atLimit,
                                              onSelect: () => duplicate(page),
                                            },
                                          ]),
                                      // the agenda page is only taken out of the menu (its eye): deleting it would leave
                                      // the Agenda on, with no page to book on
                                      ...(isAgendaPage(page)
                                        ? []
                                        : [
                                            {
                                              label: t(`${prefix}.actions.delete`),
                                              danger: true,
                                              onSelect: () => setDeleting(page),
                                            },
                                          ]),
                                    ]}
                                  />
                                </>
                              }
                            />
                          </div>
                        </PageItem>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </PageList>
            )}
          </Droppable>
        </DragDropContext>

        {/* The agenda's page before it exists: hidden, its eye adds it */}
        {!hasAgenda && (
          <PageList>
            <PageItem aria-label={t(`${prefix}.agenda.title`)}>
              <ListCard
                title={t(`${prefix}.agenda.title`)}
                description={t(`${prefix}.agenda.hidden`)}
                leading={<CalendarIcon color="currentColor" />}
                leadingVariant="icon"
                dimmed
                onClick={() => navigate("/agenda")}
                actions={
                  <ListCardAction
                    type="button"
                    title={t(`${prefix}.agenda.show`)}
                    aria-label={t(`${prefix}.agenda.show`)}
                    disabled={atLimit}
                    onClick={event => {
                      event.stopPropagation();
                      addAgenda();
                    }}>
                    <VisibilityIcon visible={false} />
                  </ListCardAction>
                }
              />
            </PageItem>
          </PageList>
        )}

        {/* The list ends with a way to add one more, as on the editor's sections */}
        {atLimit ? (
          <LimitNotice>{t(`${prefix}.limit`, { max: MAX_PAGES })}</LimitNotice>
        ) : (
          <AddSectionCard type="button" onClick={addPage}>
            <span className="icon">
              <PlusIcon width={14} height={14} color="currentColor" />
            </span>
            {t(`${prefix}.actions.add`)}
          </AddSectionCard>
        )}

        {/* The menu, after the pages it shows: a plain row (the pages are what this screen is about) */}
        <MenuRow>
          <div className="text">
            <h6>{t(`${prefix}.menu.label`)}</h6>
            <p className="description">{t(`${prefix}.menu.hint`)}</p>
          </div>
          <ToggleSwitch
            label={t(`${prefix}.menu.label`)}
            value={menuAllowed && !!site.menu?.enabled}
            disabled={!menuAllowed}
            // saved right away (in the draft): a toast says it's on or off, and that it shows once published (a FREE
            // Keepo's pages and menu never go on the air: its toast doesn't say they will)
            setValue={enabled =>
              save({ ...site, menu: { enabled } }, () =>
                toast.success(t(`${prefix}.menu.${enabled ? "turnedOn" : "turnedOff"}${isPlus ? "" : "NoPlus"}`))
              )
            }
          />
        </MenuRow>
      </Section>

      {/* Same bar as the editor's sections: "+ Página" on the left, going on to publish on the right */}
      <BarSpace />
      <FixedFooterBar>
        <BarAdd type="button" disabled={atLimit} onClick={addPage}>
          <MediumCircleContainer title={t(`${prefix}.actions.add`)} $color="var(--color-brand-blue)">
            <PlusIcon width={16} height={16} color="var(--color-neutral-white)" />
          </MediumCircleContainer>
          <h6>{t(`${prefix}.actions.page`)}</h6>
        </BarAdd>
        <ContinueButtonSection>
          <Button
            type="INFO"
            size="LG"
            // a page's sections go back to this list; from here, the style and publishing
            onClick={goOn}>
            {t("pages.editor.sections.actions.continue.button.title")}
          </Button>
        </ContinueButtonSection>
      </FixedFooterBar>

      {deleting && (
        <ConfirmModal
          type="DANGER"
          title={t(`${prefix}.delete.title`, { title: deleting.title })}
          descriptions={[t(`${prefix}.delete.description`)]}
          confirmButtonTitle={t(`${prefix}.delete.confirm`)}
          cancelTitle={t(`${prefix}.delete.cancel`)}
          onConfirm={() => {
            savePages(pages.filter(({ id }) => id !== deleting.id));
            setDeleting(undefined);
          }}
          onCancel={() => setDeleting(undefined)}
          onExit={() => setDeleting(undefined)}
        />
      )}
      {discardFailed && (
        <p className="typography-p text-red-500" role="alert">
          {t("pages.editor.sections.actions.exit.discardError")}
        </p>
      )}

      {removingDraft && <LoadingModal />}
    </Container>
  );
};

export default PagesPage;
